import { Spinner } from '@creditornot/cb-progress';
import React, { useMemo } from 'react';
import { Switch, useRouteMatch } from 'react-router-dom';

import { RouteWhenAuthenticated } from 'common/components';
import { useVenueContext } from 'modules/venue/VenueContext';

import { CampaignModuleContextProps, CampaignModuleContextProvider } from './context';
import useCampaignTypesPermittedToEdit from './hooks/useCampaignTypesPermittedToEdit';
import { DetailsCampaignPage, DetailsCampaignPageProps } from './pages/DetailsCampaignPage';
import { EditCampaignPage, EditCampaignPageProps } from './pages/EditCampaignPage';
import { ListCampaignsPage, ListCampaignsPageProps } from './pages/ListCampaignsPage/ListCampaignsPage';
import { NewCampaignPage, NewCampaignPageProps } from './pages/NewCampaignPage/NewCampaignPage';

const VenueCampaignRoutes = () => {
  const { path } = useRouteMatch();

  const venue = useVenueContext();
  const { campaignTypesPermittedToEdit, isLoading } = useCampaignTypesPermittedToEdit({
    feature: 'venue',
    featureId: venue.id,
    city: venue.city,
    entityCountry: venue.country,
    productLine: venue.product_line,
  });

  const props = useMemo(
    (): ListCampaignsPageProps & NewCampaignPageProps & DetailsCampaignPageProps & EditCampaignPageProps => ({
      merchantId: venue.merchant,
      venueIds: [venue.id],
      campaignTypesPermittedToEdit,
      timezone: venue.timezone,
      menuId: venue.menu || undefined,
      country: venue.country,
      productLines: [venue.product_line],
    }),
    [venue, campaignTypesPermittedToEdit],
  );

  const contextValue: CampaignModuleContextProps = {
    currency: venue.currency,
    entityType: 'venue',
    country: venue.country,
    venues: [venue],
    merchantId: venue.merchant,
    timezone: venue.timezone,
    isExperienceFlow: false,
  };

  if (isLoading) {
    return <Spinner size="large" />;
  }

  const { name, short_description, listimage } = venue;
  const venueDetails = { name, short_description, listimage };

  return (
    <CampaignModuleContextProvider value={contextValue}>
      <Switch>
        <RouteWhenAuthenticated exact path={path}>
          <ListCampaignsPage {...props} />
        </RouteWhenAuthenticated>

        <RouteWhenAuthenticated exact path={`${path}/new`}>
          <NewCampaignPage {...props} venueDetails={venueDetails} />
        </RouteWhenAuthenticated>

        <RouteWhenAuthenticated exact path={`${path}/:campaignId`}>
          <DetailsCampaignPage {...props} venueDetails={venueDetails} />
        </RouteWhenAuthenticated>

        <RouteWhenAuthenticated exact path={`${path}/:campaignId/edit`}>
          <EditCampaignPage {...props} venueDetails={venueDetails} />
        </RouteWhenAuthenticated>
      </Switch>
    </CampaignModuleContextProvider>
  );
};

export default VenueCampaignRoutes;
